import { RotateCcw, TrendingDown, DollarSign, Calendar } from 'lucide-react';

export function ReturnsSection({ data }) {
  // Process the data to extract return information
  const processReturnsData = (inputData) => {
    // Default structure
    const defaultData = {
      totalAmount: 0,
      totalOrders: 0,
      returns: []
    };

    if (!inputData) return defaultData;

    // Case 1: If data is already in the expected format with returns array
    if (inputData.returns && Array.isArray(inputData.returns)) {
      return {
        totalAmount: inputData.totalAmount || inputData.returns.reduce((sum, item) => sum + (item.amount || 0), 0),
        totalOrders: inputData.totalOrders || inputData.returns.length,
        returns: inputData.returns
      };
    }

    // Case 2: If data is an array (expected by original component)
    if (Array.isArray(inputData)) {
      return {
        totalAmount: inputData.reduce((sum, item) => sum + (item.amount || 0), 0),
        totalOrders: inputData.reduce((sum, item) => sum + (item.orders || 1), 0),
        returns: inputData
      };
    }

    // Case 3: If data is a summary object with SaleReturnAmount
    if (inputData.SaleReturnAmount !== undefined || inputData.saleReturnAmount !== undefined) {
      const returnAmount = inputData.SaleReturnAmount || inputData.saleReturnAmount || 0;
      
      // If there's a return amount, create a return entry
      if (returnAmount > 0) {
        // Estimate tax breakdown (you might want to adjust this based on your actual tax structure)
        const estimatedTax15 = returnAmount * 0.15; // 15% tax portion
        const estimatedTax8 = returnAmount * 0.08;  // 8% tax portion
        
        return {
          totalAmount: returnAmount,
          totalOrders: 1, // Assume 1 return order if there's a return amount
          returns: [{
            paymentMode: 'Returns',
            amount: returnAmount,
            orders: 1,
            tax15: estimatedTax15,
            tax8: estimatedTax8,
            date: new Date().toLocaleDateString()
          }]
        };
      }
    }

    // Case 4: Look for return-related data in other tables
    if (inputData.paymentModes && Array.isArray(inputData.paymentModes)) {
      // Check if any payment mode indicates returns
      const returnPayments = inputData.paymentModes.filter(item => 
        item.PaymentMode?.toLowerCase().includes('return') ||
        item.mode?.toLowerCase().includes('return') ||
        item.PaymentMode?.toLowerCase() === 'refund' ||
        item.mode?.toLowerCase() === 'refund'
      );

      if (returnPayments.length > 0) {
        const returns = returnPayments.map(item => ({
          paymentMode: item.PaymentMode || item.mode || 'Return',
          amount: item.TotalAmount || item.amount || 0,
          orders: item.OrderCount || item.count || 1,
          tax15: (item.TotalAmount || item.amount || 0) * 0.15,
          tax8: (item.TotalAmount || item.amount || 0) * 0.08
        }));

        return {
          totalAmount: returns.reduce((sum, item) => sum + item.amount, 0),
          totalOrders: returns.reduce((sum, item) => sum + item.orders, 0),
          returns: returns
        };
      }
    }

    return defaultData;
  };

  const processedData = processReturnsData(data);
  const { totalAmount, totalOrders, returns } = processedData;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Returns</h3>
          <p className="text-sm text-slate-500 mt-1">Return & refund breakdown</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl shadow-lg">
          <RotateCcw className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total Returns</p>
          <p className="text-lg font-bold text-orange-700">
            Rs {(totalAmount || 0).toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
        </div>
        <div className="p-3 bg-gradient-to-br from-red-50 to-red-100 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Return Orders</p>
          <p className="text-lg font-bold text-red-700">{totalOrders || 0}</p>
        </div>
      </div>

      <div className="space-y-3 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
        {!returns || returns.length === 0 ? (
          <div className="text-center py-8">
            <RotateCcw className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No returns</p>
          </div>
        ) : (
          returns.map((item, index) => (
            <div key={index} className="p-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">{item.paymentMode || 'Return'}</span>
                  {item.date && (
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {item.date}
                    </span>
                  )}
                </div>
                <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                  {item.orders || 1} {item.orders === 1 ? 'order' : 'orders'}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    Amount:
                  </span>
                  <span className="text-sm font-bold text-slate-900">
                    Rs {(item.amount || 0).toLocaleString(undefined, { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">15% Tax:</span>
                  <span className="text-xs font-semibold text-slate-700">
                    Rs {(item.tax15 || 0).toLocaleString(undefined, { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">8% Tax:</span>
                  <span className="text-xs font-semibold text-slate-700">
                    Rs {(item.tax8 || 0).toLocaleString(undefined, { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}